import { getClient, getApartment, checkOverduePayments } from "./salesHelpers";

/**
 * Human-readable label for a payment type enum value.
 */
export const getPaymentTypeLabel = (paymentType) => {
  const labels = {
    advanced_payment: "Advanced Payment",
    scheduled_payment: "Scheduled Payment",
    additional: "Additional Payment",
    final: "Final Payment",
    monthly: "Monthly Payment",
  };
  return (
    labels[paymentType] ||
    (paymentType
      ? paymentType
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase())
      : "")
  );
};

/**
 * Resolve the full context for a payment: its sale, client, apartment, and receipts.
 */
export const getPaymentDetails = (payment, sales, clients, apartments, receipts) => {
  const sale = sales.find((s) => s.id === payment.sale_id);
  // sale.client_id / sale.apartment_id may already be populated objects
  // (backend .populate()) rather than plain ID strings — getClient/getApartment
  // handle both shapes; a naive `.find(x => x.id === sale.client_id)` here
  // would never match a populated object and silently return undefined.
  const client = sale ? getClient(sale.client_id, clients) : null;
  const apartment = sale ? getApartment(sale.apartment_id, apartments) : null;
  const paymentReceipts = receipts.filter((r) => r.payment_id === payment.id);
  return { sale, client, apartment, receipts: paymentReceipts };
};

/**
 * Cumulative amount paid on a sale as of (and including) a specific
 * payment, ordered chronologically by payment_date. This is the single
 * source of truth both getRemainingBalanceAfterPayment and the payments
 * export use — deriving "total paid so far" and "remaining balance" from
 * two different calculations is how they end up not summing to the sale's
 * total price.
 */
export const getCumulativePaidAsOfPayment = (payment, allPayments) => {
  const salePayments = allPayments
    .filter((p) => p.sale_id === payment.sale_id)
    .sort((a, b) => {
      const dateA = new Date(a.payment_date);
      const dateB = new Date(b.payment_date);
      return dateA - dateB || (a.id || "").localeCompare(b.id || "");
    });

  let cumulativePaid = 0;
  for (const p of salePayments) {
    cumulativePaid += p.amount || 0;
    if (p.id === payment.id) break;
  }
  return cumulativePaid;
};

/**
 * Compute the remaining balance on the sale AFTER this specific payment
 * (cumulative sum up to and including this payment).
 */
export const getRemainingBalanceAfterPayment = (payment, sale, allPayments) => {
  if (!sale) return null;
  const cumulativePaid = getCumulativePaidAsOfPayment(payment, allPayments);
  return Math.max(0, (sale.total_price || 0) - cumulativePaid);
};

/**
 * Calculate whether a payment was delayed and by how many days,
 * based on the sale's payment schedule.
 */
export const calculatePaymentDelayInfo = (payment, sale, allPayments) => {
  if (!sale || !payment) return { isDelayed: false, delayDays: 0, dueDate: null };

  const salePayments = allPayments
    .filter((p) => p.sale_id === sale.id)
    .sort((a, b) => {
      const dateA = new Date(a.payment_date);
      const dateB = new Date(b.payment_date);
      return dateA - dateB || (a.id || "").localeCompare(b.id || "");
    });

  const index = salePayments.findIndex((p) => p.id === payment.id);
  if (index === -1) return { isDelayed: false, delayDays: 0, dueDate: null };

  const saleDate = new Date(sale.sale_date);
  saleDate.setHours(0, 0, 0, 0);
  const frequency = sale.payment_frequency_months || 1;

  const dueDate = new Date(saleDate);
  dueDate.setMonth(dueDate.getMonth() + index * frequency);

  const paymentDate = new Date(payment.payment_date);
  paymentDate.setHours(0, 0, 0, 0);

  const isDelayed = paymentDate > dueDate;
  const delayDays = isDelayed
    ? Math.floor((paymentDate - dueDate) / (1000 * 60 * 60 * 24))
    : 0;

  return { isDelayed, delayDays, dueDate };
};

/**
 * Whether a payment should be shown as "overdue" — based on whether the
 * SALE it belongs to is currently behind on its expected balance (the same
 * definition used on the Sales tab, via checkOverduePayments), not whether
 * this specific payment was made after its own scheduled due date.
 *
 * This means every payment under the same sale shows the same status, and
 * it clears the moment the client catches up — unlike calculatePaymentDelayInfo
 * above, which stays "delayed" permanently once a payment is made late,
 * regardless of what happens afterward.
 */
export const getPaymentOverdueStatus = (sale) => {
  if (!sale) return { isDelayed: false, missedPayments: 0, amountOverdue: 0 };
  const overdueInfo = checkOverduePayments(sale);
  return {
    isDelayed: overdueInfo?.isOverdue ?? false,
    missedPayments: overdueInfo?.missedPayments ?? 0,
    amountOverdue: overdueInfo?.amountOverdue ?? 0,
  };
};